// Mock chat service — frontend-only stand-in for the approved chatbot provider.
//
// INTEGRATION CONTRACT (implemented by Cursor):
//
//   sendChatMessage({ conversationId, message }) -> Promise<{
//     reply: string,
//     suggestions?: string[],
//     handoff?: boolean          // true when the visitor should be routed to enquiry
//   }>
//
// Server responsibilities (Cursor): rate limiting, approved-knowledge grounding,
// no system-prompt or credential exposure, privacy-aware logging, escalation to
// the human enquiry path, and the same content boundaries enforced here:
// only MoveOS, myBantu, LearnSphere, CraneSoftwares and ZasyaOnline services.

import { PRODUCTS } from "../data/content";

const wait = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, process.env.NODE_ENV === "test" ? 0 : ms);
  });

const productReply = (p) =>
  `${p.name} is our ${p.category} product — "${p.headline}" ${p.shortDescription} You can explore it on the ${p.name} website, or I can point you to the demo request form.`;

const FALLBACK = {
  reply:
    "I'm not able to verify an answer to that from our approved content, so I'd rather not guess. I can connect you with the team through our enquiry form and they'll take it from there.",
  suggestions: [],
  handoff: true,
};

const RULES = [
  {
    match: /mindvault|ztools/i,
    reply: () => FALLBACK.reply,
    suggestions: [],
    handoff: true,
  },
  {
    match: /\b(price|pricing|cost|quote)\b/i,
    reply: () =>
      "The fastest route is our enquiry form — tell us which product or service you're interested in and the team will respond. For product demos, each product page has a Book a Demo option. I don't have access to pricing; the team will handle that.",
    suggestions: [],
    handoff: true,
  },
  {
    match: /moveos|fleet|transport|truck|vehicle/i,
    reply: () => productReply(PRODUCTS[0]),
    suggestions: ["Book a MoveOS demo", "Compare all products"],
  },
  {
    match: /mybantu|bantu|call centre|call center|phone|salon|clinic|cleaning/i,
    reply: () =>
      `${productReply(PRODUCTS[1])} Plans and pricing are published on the myBantu website rather than here.`,
    suggestions: ["Visit myBantu website", "Talk to the team"],
  },
  {
    match: /learnsphere|learning|training|course|certif|lms/i,
    reply: () =>
      `${productReply(PRODUCTS[2])} Its learning lifecycle runs Create → Deliver → Assess → Certify → Track.`,
    suggestions: ["Explore LearnSphere", "Talk to the team"],
  },
  {
    match: /crane|heavy equipment|rental|invoice|vahan|sarathi/i,
    reply: () => productReply(PRODUCTS[3]),
    suggestions: ["Explore CraneSoftwares", "Talk to the team"],
  },
  {
    match: /consult|service|integration|infrastructure|custom software|automation|develop/i,
    reply: () =>
      "ZasyaOnline also delivers consulting and solutions: custom software development, enterprise integration and solution architecture, infrastructure and managed services, contact-centre consulting and implementation, AI-powered business solutions, and enterprise platform consulting. The Consulting & Solutions page has the details.",
    suggestions: ["See Consulting & Solutions", "Talk to the team"],
  },
  {
    match: /customer|client|reference|who.*work|case/i,
    reply: () =>
      "Our engagements include customised software for Sanghvi Movers, Pune Logitech and Indian Railways; MoveOS deployments for Sudhir Cranes and Raja Transport; infrastructure services for Procell Biologics; and contact-centre consulting and implementation for Tashi Cell and Bank of Bhutan.",
    suggestions: ["View Customers page", "Talk to the team"],
  },
  {
    match: /demo|contact|talk|human|email|phone number/i,
    reply: () =>
      "The fastest route is our enquiry form — tell us which product or service you're interested in and the team will respond. For product demos, each product page has a Book a Demo option. I don't have access to pricing; the team will handle that.",
    suggestions: [],
    handoff: true,
  },
  {
    match: /about|who are you|company|zasya/i,
    reply: () =>
      "ZasyaOnline Pvt Ltd is a product-led technology company. We build our own software products — MoveOS, myBantu, LearnSphere and CraneSoftwares — and deliver enterprise consulting, custom development, integration and managed technology services.",
    suggestions: ["About ZasyaOnline", "Compare all products"],
  },
];

export const CHAT_SUGGESTIONS = [
  "What products does ZasyaOnline offer?",
  "Tell me about MoveOS",
  "What services do you provide?",
  "How do I talk to the team?",
];

export async function sendChatMessage({ message }) {
  await wait(700 + Math.random() * 500);
  const rule = RULES.find((r) => r.match.test(message));
  if (!rule) return { ...FALLBACK };
  const reply = typeof rule.reply === "function" ? rule.reply() : rule.reply;
  return { reply, suggestions: rule.suggestions || [], handoff: !!rule.handoff };
}
